import React, { useEffect, useRef } from "react";
import { Alert, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetLazy } from "../../api";
import { EmptyState } from "../../components/ui";
import { PartnerCell } from "./partner-cell";
import { ICouponQuery, queryParams } from "./coupon-query";
import {
  COUPON_RARELY_MIN_CHECKED,
  ICouponInsightsResponse,
  ICouponRateCounts,
} from "../../interfaces";

/**
 * Whether coupons work, counted three ways. A quoted "20% working" is only
 * comparable once you know which denominator it was taken over.
 */

const AXIS = { fontSize: 12, stroke: "#adb5bd" };
// Status colours carry meaning; the rate lines use categorical slots 1–3.
const GOOD = "#198754";
const BAD = "#dc3545";
const RATE_SERIES = [
  {
    key: "checkoutRate",
    label: "Checkouts with a discount",
    colour: "#2a78d6",
  },
  { key: "attemptRate", label: "Code attempts that worked", colour: "#eb6834" },
  {
    key: "codeRate",
    label: "Codes that worked in that period",
    colour: "#1baf7a",
  },
] as const;
const HOUR_MS = 3600 * 1000;

const ratio = (n: number, d: number) => (d > 0 ? n / d : null);
const rate = (n: number, d: number) => {
  const r = ratio(n, d);
  return r == null ? null : Math.round(r * 1000) / 10;
};
const pct = (n: number, d: number) => {
  const r = ratio(n, d);
  return r == null ? "—" : `${(r * 100).toFixed(r > 0 && r < 0.01 ? 2 : 1)}%`;
};
const num = (n: number) => (n ?? 0).toLocaleString();

/** A level of the funnel: its total split into worked and didn't. */
const FunnelRow: React.FC<{
  label: string;
  hint: string;
  total: number;
  worked: number;
  sub?: boolean;
}> = ({ label, hint, total, worked, sub }) => (
  <tr className={sub ? "" : "table-light fw-semibold"} title={hint}>
    <td className={sub ? "ps-4" : ""}>{label}</td>
    <td className="text-end">{num(total)}</td>
    <td className="text-end">{num(worked)}</td>
    <td className="text-end">{num(total - worked)}</td>
    <td className="text-end text-success">{pct(worked, total)}</td>
    <td className="text-end text-danger">{pct(total - worked, total)}</td>
  </tr>
);

/** One slice of the level above, shown as a share of that level's total. */
const FunnelPart: React.FC<{
  label: string;
  n: number;
  of: number;
  worked?: boolean;
}> = ({ label, n, of, worked }) => (
  <tr className="text-muted">
    <td className="ps-4">↳ {label}</td>
    <td />
    <td className="text-end">{worked ? num(n) : ""}</td>
    <td className="text-end">{worked ? "" : num(n)}</td>
    <td className="text-end">{worked ? pct(n, of) : ""}</td>
    <td className="text-end">{worked ? "" : pct(n, of)}</td>
  </tr>
);

/** The columns every breakdown shares, so the tables read the same way. */
const RateHead: React.FC = () => (
  <>
    <th className="text-end" title="Extension events — one per checkout">
      Checkouts
    </th>
    <th
      className="text-end"
      title="Share of checkouts where at least one code applied"
    >
      Got a discount
    </th>
    <th className="text-end" title="Every code submitted counts once">
      Attempts
    </th>
    <th className="text-end" title="Share of attempts the shop accepted">
      Worked
    </th>
    <th
      className="text-end"
      title="Attempts that never found the coupon box — our template, not the code"
    >
      No coupon box
    </th>
  </>
);

const RateCells: React.FC<{ r: ICouponRateCounts }> = ({ r }) => (
  <>
    <td className="text-end">{num(r.checkouts)}</td>
    <td className="text-end">{pct(r.checkoutsWithApply, r.checkouts)}</td>
    <td className="text-end">{num(r.attempts)}</td>
    <td className="text-end">{pct(r.applied, r.attempts)}</td>
    <td className="text-end">{pct(r.templateBlocked, r.attempts)}</td>
  </>
);

const Section: React.FC<{
  title: string;
  note?: string;
  children: React.ReactNode;
}> = ({ title, note, children }) => (
  <Card className="mb-3">
    <Card.Body className="pb-0">
      <h6 className="mb-1">{title}</h6>
      {note && <p className="small text-muted mb-2">{note}</p>}
    </Card.Body>
    {children}
  </Card>
);

export const CouponInsights: React.FC<{
  query: ICouponQuery | null;
  active: boolean;
  onPartner: (partnerId: string, partnerName: string) => void;
  onBrand: (brandId: string, brandName: string) => void;
  onError: (message: string) => void;
}> = ({ query, active, onPartner, onBrand, onError }) => {
  const navigate = useNavigate();
  const { execute, data, loading, error } =
    useGetLazy<ICouponInsightsResponse>();
  const fetchedFor = useRef<ICouponQuery | null>(null);

  useEffect(() => {
    if (!active || !query || fetchedFor.current === query) return;
    fetchedFor.current = query;
    const p = queryParams(query, "insights");
    execute(`/coupon-performance/insights?${p.toString()}`).catch(() => null);
  }, [active, query, execute]);

  if (!query)
    return (
      <EmptyState
        icon="bi-graph-up"
        title="Nothing loaded yet"
        message="Set the filters above, then press Search."
      />
    );
  if (loading && !data)
    return (
      <div className="text-center py-5">
        <Spinner />
      </div>
    );
  if (error)
    return (
      <Alert variant="danger" className="small">
        Could not build insights for this window: {error.message}
      </Alert>
    );
  if (!data) return null;

  const t = data.totals;
  const perCheckout = t.checkouts ? t.attempts / t.checkouts : 0;
  const checked = t.attempts - t.templateBlocked;
  const minPct = Math.round(data.minRate * 100);
  const totalFailed = t.checkouts - t.checkoutsWithApply;
  let running = 0;
  const brands = data.topFailingBrands.map((r) => {
    const failed = r.checkouts - r.checkoutsWithApply;
    running += failed;
    return { ...r, failed, running };
  });
  // Hours show in local time; days stay UTC dates, as the server buckets them.
  const hourly = data.bucket === "hour";
  const unit = hourly ? "hour" : "day";
  const startOf = (p: string) => Date.parse(hourly ? p : `${p}T00:00:00Z`);
  const tick = (p: string) =>
    hourly
      ? new Date(p).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : p;
  const label = (p: string) =>
    hourly
      ? new Date(p).toLocaleString([], {
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : p;
  const timeline = data.timeline.map((d) => ({
    period: d.period,
    withDiscount: d.checkoutsWithApply,
    without: d.checkouts - d.checkoutsWithApply,
    checkoutRate: rate(d.checkoutsWithApply, d.checkouts),
    attemptRate: rate(d.applied, d.attempts),
    codeRate: rate(d.workingCodes, d.codes),
  }));
  const size = hourly ? HOUR_MS : 24 * HOUR_MS;
  const partial =
    timeline.length > 0 &&
    (startOf(timeline[0].period) < Date.parse(data.window.from) ||
      startOf(timeline[timeline.length - 1].period) + size >
        Date.parse(data.window.to));

  if (!t.attempts)
    return (
      <EmptyState
        title="No coupon activity in this window"
        message="Try a wider window or another partner."
      />
    );

  return (
    <>
      <Section
        title="Did it work?"
        note="The same rows counted three ways, each split into what worked and what didn't."
      >
        <Table size="sm" responsive className="mb-0 small align-middle">
          <thead>
            <tr>
              <th />
              <th className="text-end">Total</th>
              <th className="text-end">Worked</th>
              <th className="text-end">Didn&apos;t work</th>
              <th className="text-end">Worked %</th>
              <th className="text-end">Didn&apos;t work %</th>
            </tr>
          </thead>
          <tbody>
            <FunnelRow
              label="Checkouts"
              hint="One per extension event. Did the shopper end up with a code that worked?"
              total={t.checkouts}
              worked={t.checkoutsWithApply}
            />
            <FunnelPart
              label="every code tried was rejected by the shop"
              n={t.checkouts - t.checkoutsWithApply - t.checkoutsAllBlocked}
              of={t.checkouts}
            />
            <FunnelPart
              label="never found the coupon box (our template)"
              n={t.checkoutsAllBlocked}
              of={t.checkouts}
            />
            <FunnelRow
              label="Code attempts"
              hint="Every code submitted counts once."
              total={t.attempts}
              worked={t.applied}
            />
            <FunnelPart
              label="rejected by the shop"
              n={checked - t.applied}
              of={t.attempts}
            />
            <FunnelPart
              label="never found the coupon box (our template)"
              n={t.templateBlocked}
              of={t.attempts}
            />
            <FunnelRow
              label="Attempts the shop checked"
              hint="Code attempts without the ones that never reached a coupon box — how often the shop accepted the code itself."
              total={checked}
              worked={t.applied}
              sub
            />
            <FunnelRow
              label="Codes tried"
              hint="Distinct code and brand pairs. Worked = applied at least once in the window."
              total={t.codes}
              worked={t.workingCodes}
            />
            <FunnelPart
              label={`worked on at least ${minPct}% of checked attempts`}
              n={t.workingCodes - t.rarelyCodes}
              of={t.codes}
              worked
            />
            <FunnelPart
              label={`worked, but on under ${minPct}% (rarely works)`}
              n={t.rarelyCodes}
              of={t.codes}
              worked
            />
          </tbody>
        </Table>
        <Card.Body className="pt-2">
          <ul className="small mb-0 text-muted">
            <li>
              {perCheckout < 1.2
                ? `Almost every checkout tries a single code (${perCheckout.toFixed(2)} on average), so the attempt and checkout rates say the same thing.`
                : `Checkouts try ${perCheckout.toFixed(1)} codes on average, which pulls the attempt rate below the checkout rate.`}
            </li>
            <li>
              {minPct}% is the &ldquo;Working means ≥&rdquo; setting in the
              filter bar. A code needs at least {COUPON_RARELY_MIN_CHECKED}{" "}
              checked attempts before it can be called rare.
            </li>
            {t.rowsWithoutCode > 0 && (
              <li>
                {num(t.rowsWithoutCode)} rows carried no coupon code and are
                left out of every figure here.
              </li>
            )}
          </ul>
        </Card.Body>
      </Section>

      <Row className="g-3">
        <Col lg={6}>
          <Section
            title={`Checkouts per ${unit}`}
            note={`Stacked: the top edge is all checkouts that tried a code.${
              partial
                ? ` The first and last ${unit} only partly fall inside the window.`
                : ""
            }`}
          >
            <Card.Body className="pt-0">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={timeline}
                  margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e9ecef"
                    vertical={false}
                  />
                  <XAxis dataKey="period" tick={AXIS} tickFormatter={tick} />
                  <YAxis tick={AXIS} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(p: any) => label(String(p))}
                    formatter={(v: any, name: any) => [num(v), name]}
                  />
                  <Legend />
                  <Bar
                    dataKey="withDiscount"
                    name="Got a discount"
                    stackId="c"
                    fill={GOOD}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                  <Bar
                    dataKey="without"
                    name="No discount"
                    stackId="c"
                    fill={BAD}
                    stroke="#fff"
                    strokeWidth={1}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Section>
        </Col>
        <Col lg={6}>
          <Section
            title={`Success rate per ${unit}`}
            note={`Three denominators side by side. When checkouts try several codes, the attempt line sits below the checkout line.${
              hourly ? " Gaps are hours with no activity." : ""
            }`}
          >
            <Card.Body className="pt-0">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={timeline}
                  margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e9ecef"
                    vertical={false}
                  />
                  <XAxis dataKey="period" tick={AXIS} tickFormatter={tick} />
                  <YAxis tick={AXIS} domain={[0, 100]} unit="%" />
                  <Tooltip
                    labelFormatter={(p: any) => label(String(p))}
                    formatter={(v: any, name: any) => [
                      v == null ? "—" : `${v}%`,
                      name,
                    ]}
                  />
                  <Legend />
                  {RATE_SERIES.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.colour}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Section>
        </Col>
      </Row>

      <Section
        title={hourly ? "Hour by hour" : "Day by day"}
        note="The numbers behind both charts."
      >
        <Table size="sm" hover responsive className="mb-0 small">
          <thead>
            <tr>
              <th>{hourly ? "Hour" : "Day"}</th>
              <RateHead />
              <th className="text-end">Codes</th>
              <th
                className="text-end"
                title={`Codes that applied at least once that ${unit}`}
              >
                Codes working
              </th>
            </tr>
          </thead>
          <tbody>
            {data.timeline.map((d) => (
              <tr key={d.period}>
                <td>{label(d.period)}</td>
                <RateCells r={d} />
                <td className="text-end">{num(d.codes)}</td>
                <td className="text-end">{pct(d.workingCodes, d.codes)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Row className="g-3">
        <Col lg={6}>
          <Section
            title="Codes tried per checkout"
            note="How many codes each checkout tried, and how often it ended with a discount."
          >
            <Table size="sm" responsive className="mb-0 small">
              <thead>
                <tr>
                  <th>Codes tried</th>
                  <th className="text-end">Checkouts</th>
                  <th className="text-end">Got a discount</th>
                  <th className="text-end">Attempts</th>
                  <th className="text-end">Worked</th>
                </tr>
              </thead>
              <tbody>
                {data.checkoutSizes.map((s) => (
                  <tr key={s.bucket}>
                    <td>{s.bucket}</td>
                    <td className="text-end">{num(s.checkouts)}</td>
                    <td className="text-end">
                      {pct(s.checkoutsWithApply, s.checkouts)}
                    </td>
                    <td className="text-end">{num(s.attempts)}</td>
                    <td className="text-end">{pct(s.applied, s.attempts)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>
        <Col lg={6}>
          <Section
            title="By partner"
            note="Click a partner to filter the whole page to it."
          >
            <Table size="sm" hover responsive className="mb-0 small">
              <thead>
                <tr>
                  <th>Partner</th>
                  <RateHead />
                </tr>
              </thead>
              <tbody>
                {data.byPartner.map((r) => (
                  <tr
                    key={r.partnerId}
                    style={{ cursor: "pointer" }}
                    title={`Filter to this partner (${r.partnerId})`}
                    onClick={() => onPartner(r.partnerId, r.name ?? "")}
                  >
                    <td>{r.name ?? r.partnerId}</td>
                    <RateCells r={r} />
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
          <Section title="By platform">
            <Table size="sm" responsive className="mb-0 small">
              <thead>
                <tr>
                  <th>Platform</th>
                  <RateHead />
                </tr>
              </thead>
              <tbody>
                {data.byPlatform.map((r) => (
                  <tr key={r.platform}>
                    <td>{r.platform || "—"}</td>
                    <RateCells r={r} />
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>
      </Row>

      <Section
        title="Brands with the most checkouts that got no discount"
        note={`Share and running total are of all ${num(totalFailed)} checkouts without a discount in this filter. Click a brand to see its codes.`}
      >
        <Table size="sm" hover responsive className="mb-0 small">
          <thead>
            <tr>
              <th>Brand</th>
              <th title="Hover for the split by partner">Partner</th>
              <th className="text-end">No discount</th>
              <th
                className="text-end"
                title="This brand's checkouts without a discount, as a share of all of them"
              >
                Share of failures
              </th>
              <th
                className="text-end"
                title="Running total down the list: how much of all failures these brands account for together"
              >
                Running total
              </th>
              <RateHead />
            </tr>
          </thead>
          <tbody>
            {brands.map((r) => (
              <tr
                key={r.brandId ?? "none"}
                style={{ cursor: r.brandId ? "pointer" : undefined }}
                title={
                  r.brandId
                    ? `Show this brand's codes (${r.brandId})`
                    : "No brand id was sent"
                }
                onClick={() =>
                  r.brandId && onBrand(r.brandId, r.brandName ?? "")
                }
              >
                <td>
                  {r.brandName ?? r.brandId ?? (
                    <span className="text-muted">No brand id</span>
                  )}
                </td>
                <td>
                  <PartnerCell partners={r.partners} />
                </td>
                <td className="text-end fw-semibold">{num(r.failed)}</td>
                <td className="text-end">{pct(r.failed, totalFailed)}</td>
                <td className="text-end">{pct(r.running, totalFailed)}</td>
                <RateCells r={r} />
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Section
        title="Codes with the most rejected attempts"
        note="Click a code to open its history."
      >
        <Table size="sm" hover responsive className="mb-0 small">
          <thead>
            <tr>
              <th>Code</th>
              <th>Brand</th>
              <th title="Hover for the split by partner">Partner</th>
              <th className="text-end">Rejected</th>
              <th className="text-end">Attempts</th>
              <th className="text-end">Worked</th>
              <th
                className="text-end"
                title="Attempts that never found the coupon box"
              >
                No coupon box
              </th>
              <th className="text-end">Checkouts</th>
              <th>What the shop said</th>
            </tr>
          </thead>
          <tbody>
            {data.topFailingCodes.map((r) => (
              <tr
                key={`${r.code}-${r.brandId ?? "none"}`}
                style={{ cursor: "pointer" }}
                title="Open this code's history"
                onClick={() =>
                  navigate(
                    `/coupon-performance/${encodeURIComponent(r.code)}?` +
                      new URLSearchParams(
                        r.brandId ? { brandId: r.brandId } : {}
                      ).toString()
                  )
                }
              >
                <td className="font-monospace">{r.code}</td>
                <td>{r.brandName ?? r.brandId ?? "—"}</td>
                <td>
                  <PartnerCell partners={r.partners} />
                </td>
                <td className="text-end fw-semibold">
                  {num(r.attempts - r.applied)}
                </td>
                <td className="text-end">{num(r.attempts)}</td>
                <td className="text-end">{pct(r.applied, r.attempts)}</td>
                <td className="text-end">
                  {pct(r.templateBlocked, r.attempts)}
                </td>
                <td className="text-end">{num(r.checkouts)}</td>
                <td
                  className="text-muted text-truncate"
                  style={{ maxWidth: 320 }}
                  title={r.lastError ?? ""}
                >
                  {r.lastError ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Row className="g-3">
        <Col lg={8}>
          <Section
            title="What the shops said"
            note="Every failed attempt's message, most frequent first. Click one to see the raw rows."
          >
            <Table size="sm" hover responsive className="mb-0 small">
              <thead>
                <tr>
                  <th>Message</th>
                  <th title="Hover for the split by partner">Partner</th>
                  <th className="text-end">Attempts</th>
                  <th className="text-end">Share of failures</th>
                  <th className="text-end">Codes</th>
                  <th className="text-end">Brands</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.topErrors.map((r) => (
                  <tr
                    key={r.message}
                    style={{ cursor: "pointer" }}
                    title="Show the raw rows with this message"
                    onClick={() => onError(r.message)}
                  >
                    <td>{r.message}</td>
                    <td>
                      <PartnerCell partners={r.partners} />
                    </td>
                    <td className="text-end">{num(r.attempts)}</td>
                    <td className="text-end">
                      {pct(r.attempts, t.attempts - t.applied)}
                    </td>
                    <td className="text-end">{num(r.codes)}</td>
                    <td className="text-end">{num(r.brands)}</td>
                    <td>{new Date(r.lastSeen).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>
        <Col lg={4}>
          <Section
            title="By shopper country"
            note="Where the shopper was, not the merchant's market. Busiest 20."
          >
            <Table size="sm" responsive className="mb-0 small">
              <thead>
                <tr>
                  <th>Country</th>
                  <th className="text-end">Checkouts</th>
                  <th className="text-end">Got a discount</th>
                  <th className="text-end">Worked</th>
                </tr>
              </thead>
              <tbody>
                {data.byCountry.map((r) => (
                  <tr key={r.country}>
                    <td>{r.country || "—"}</td>
                    <td className="text-end">{num(r.checkouts)}</td>
                    <td className="text-end">
                      {pct(r.checkoutsWithApply, r.checkouts)}
                    </td>
                    <td className="text-end">{pct(r.applied, r.attempts)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>
      </Row>
    </>
  );
};
